import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { 
  StockItem, 
  StockBatch, 
  StockItemWithBatches, 
  CustomSupplier,
  DEFAULT_SUPPLIERS
} from "@/types/catering";
import { formatQuantityWithUnit } from "@/lib/unitPluralize";

export function useCateringStock(ptaId: string | null) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [stockItems, setStockItems] = useState<StockItemWithBatches[]>([]);
  const [customSuppliers, setCustomSuppliers] = useState<CustomSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);

  // Fetch user's name for logging
  useEffect(() => {
    const fetchUserName = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('members')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.full_name) {
        setUserName(data.full_name);
      }
    };
    fetchUserName();
  }, [user?.id]);

  // Helper to log stock activity
  const logActivity = async (
    actionType: string,
    itemName: string,
    options?: {
      batchNumber?: string | null;
      quantity?: number | null;
      eventName?: string | null;
      details?: Record<string, any>;
    }
  ) => {
    if (!ptaId) return;
    
    try {
      await supabase.from('catering_stock_log').insert({
        pta_id: ptaId,
        user_id: user?.id || null,
        user_name: userName,
        action_type: actionType,
        item_name: itemName,
        batch_number: options?.batchNumber || null,
        quantity: options?.quantity ?? null,
        event_name: options?.eventName || null,
        details: options?.details ? JSON.stringify(options.details) : null
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  const fetchStockItems = useCallback(async () => {
    if (!ptaId) return;
    setLoading(true);

    // Fetch stock items
    const { data: items, error: itemsError } = await supabase
      .from('catering_stock_items')
      .select('*')
      .eq('pta_id', ptaId)
      .order('item_name');

    if (itemsError) {
      console.error('Error fetching stock items:', itemsError);
      setLoading(false);
      return;
    }

    // Fetch all batches for this PTA
    const { data: batches, error: batchesError } = await supabase
      .from('catering_stock_batches')
      .select('*')
      .eq('pta_id', ptaId)
      .order('expiry_date', { ascending: true, nullsFirst: false });

    if (batchesError) {
      console.error('Error fetching batches:', batchesError);
      setLoading(false);
      return;
    }

    // Combine items with their batches
    const itemsWithBatches: StockItemWithBatches[] = (items || []).map(item => {
      const itemBatches = (batches || []).filter(b => b.stock_item_id === item.id);
      const totalQuantity = itemBatches.reduce((sum, b) => sum + b.quantity, 0);
      return {
        ...item,
        batches: itemBatches,
        totalQuantity
      };
    });

    setStockItems(itemsWithBatches);
    setLoading(false);
  }, [ptaId]);

  const fetchCustomSuppliers = useCallback(async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('catering_custom_suppliers')
      .select('id, name')
      .eq('pta_id', ptaId)
      .order('name');

    if (!error && data) {
      setCustomSuppliers(data);
    }
  }, [ptaId]);

  useEffect(() => {
    if (ptaId) {
      fetchStockItems();
      fetchCustomSuppliers();
    }
  }, [ptaId, fetchStockItems, fetchCustomSuppliers]);

  const addCustomSupplier = async (name: string): Promise<boolean> => {
    if (!name.trim() || !ptaId) return false;
    
    const { data, error } = await supabase
      .from('catering_custom_suppliers')
      .insert({ pta_id: ptaId, name: name.trim() })
      .select('id, name')
      .single();

    if (!error && data) {
      setCustomSuppliers(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      return true;
    }
    return false;
  };

  const getAllSupplierOptions = () => {
    const customNames = customSuppliers.map(s => s.name);
    return [...DEFAULT_SUPPLIERS, ...customNames];
  };

  const addStockItem = async (data: {
    item_name: string;
    supplier: string | null;
    unit_type: string;
    low_stock_threshold: number;
  }): Promise<string | null> => {
    if (!ptaId) return null;

    const { data: newItem, error } = await supabase
      .from('catering_stock_items')
      .insert({
        pta_id: ptaId,
        item_name: data.item_name,
        supplier: data.supplier,
        unit_type: data.unit_type,
        low_stock_threshold: data.low_stock_threshold
      })
      .select()
      .single();

    if (error) {
      toast({ title: "Error", description: "Failed to add stock item", variant: "destructive" });
      return null;
    }

    await logActivity('item_added', data.item_name, {
      details: { supplier: data.supplier, unit_type: data.unit_type, low_stock_threshold: data.low_stock_threshold }
    });

    toast({ title: "Item Created", description: "Stock item created. Now add a batch." });
    await fetchStockItems();
    return newItem?.id || null;
  };

  const updateStockItem = async (itemId: string, data: {
    item_name: string;
    supplier: string | null;
    unit_type: string;
    low_stock_threshold: number;
  }): Promise<boolean> => {
    const { error } = await supabase
      .from('catering_stock_items')
      .update({
        item_name: data.item_name,
        supplier: data.supplier,
        unit_type: data.unit_type,
        low_stock_threshold: data.low_stock_threshold
      })
      .eq('id', itemId);

    if (error) {
      toast({ title: "Error", description: "Failed to update stock item", variant: "destructive" });
      return false;
    }

    await logActivity('item_updated', data.item_name, {
      details: { supplier: data.supplier, unit_type: data.unit_type, low_stock_threshold: data.low_stock_threshold }
    });

    toast({ title: "Item Updated", description: "Stock item has been updated" });
    await fetchStockItems();
    return true;
  };

  const deleteStockItem = async (itemId: string, itemName?: string): Promise<boolean> => {
    const item = stockItems.find(i => i.id === itemId);
    const name = itemName || item?.item_name || 'Unknown';
    
    const { error } = await supabase
      .from('catering_stock_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      toast({ title: "Error", description: "Failed to delete stock item", variant: "destructive" });
      return false;
    }

    await logActivity('item_deleted', name);

    toast({ title: "Item Deleted", description: "Stock item and all batches have been removed" });
    await fetchStockItems();
    return true;
  };

  const addBatch = async (stockItemId: string, itemName: string, data: {
    batch_number: string | null;
    quantity: number;
    expiry_date: string | null;
  }): Promise<boolean> => {
    if (!ptaId) return false;

    const status = data.quantity === 0 ? 'out_of_stock' : 'in_stock';
    const item = stockItems.find(i => i.id === stockItemId);
    const unitType = item?.unit_type || 'units';

    const { error } = await supabase
      .from('catering_stock_batches')
      .insert({
        stock_item_id: stockItemId,
        pta_id: ptaId,
        batch_number: data.batch_number,
        quantity: data.quantity,
        expiry_date: data.expiry_date,
        status
      });

    if (error) {
      toast({ title: "Error", description: "Failed to add batch", variant: "destructive" });
      return false;
    }

    await logActivity('batch_added', itemName, {
      batchNumber: data.batch_number,
      quantity: data.quantity,
      details: { unit_type: unitType, expiry_date: data.expiry_date }
    });

    toast({ title: "Batch Added", description: "New batch has been added to stock" });
    await fetchStockItems();
    return true;
  };

  const updateBatch = async (batchId: string, itemName: string, data: {
    batch_number: string | null;
    quantity: number;
    expiry_date: string | null;
  }): Promise<boolean> => {
    const status = data.quantity === 0 ? 'out_of_stock' : 'in_stock';

    // Find the item to get unit type
    let unitType = 'units';
    for (const item of stockItems) {
      const batch = item.batches.find(b => b.id === batchId);
      if (batch) {
        unitType = item.unit_type || 'units';
        break;
      }
    }

    const { error } = await supabase
      .from('catering_stock_batches')
      .update({
        batch_number: data.batch_number,
        quantity: data.quantity,
        expiry_date: data.expiry_date,
        status
      })
      .eq('id', batchId);

    if (error) {
      toast({ title: "Error", description: "Failed to update batch", variant: "destructive" });
      return false;
    }

    await logActivity('batch_updated', itemName, {
      batchNumber: data.batch_number,
      quantity: data.quantity,
      details: { unit_type: unitType, expiry_date: data.expiry_date }
    });

    toast({ title: "Batch Updated", description: "Batch has been updated" });
    await fetchStockItems();
    return true;
  };

  const deleteBatch = async (batchId: string, itemName: string, batchNumber?: string | null): Promise<boolean> => {
    const { error } = await supabase
      .from('catering_stock_batches')
      .delete()
      .eq('id', batchId);

    if (error) {
      toast({ title: "Error", description: "Failed to delete batch", variant: "destructive" });
      return false;
    }

    await logActivity('batch_deleted', itemName, {
      batchNumber: batchNumber
    });

    toast({ title: "Batch Deleted", description: "Batch has been removed" });
    await fetchStockItems();
    return true;
  };

  // Assign stock to event - deducts from oldest batches (FIFO)
  const assignToEvent = async (
    stockItemId: string, 
    eventId: string, 
    quantity: number,
    eventName?: string
  ): Promise<boolean> => {
    if (!ptaId) return false;

    const item = stockItems.find(i => i.id === stockItemId);
    if (!item) {
      toast({ title: "Error", description: "Stock item not found", variant: "destructive" });
      return false;
    }

    if (item.totalQuantity < quantity) {
      toast({ title: "Error", description: "Not enough stock available", variant: "destructive" });
      return false;
    }

    // Deduct from batches (oldest expiry first)
    let remaining = quantity;
    const updates: { batchId: string; newQuantity: number }[] = [];

    for (const batch of item.batches) {
      if (remaining <= 0) break;
      
      const deduct = Math.min(batch.quantity, remaining);
      updates.push({ batchId: batch.id, newQuantity: batch.quantity - deduct });
      remaining -= deduct;
    }

    // Update batches
    for (const update of updates) {
      const status = update.newQuantity === 0 ? 'out_of_stock' : 'in_stock';
      await supabase
        .from('catering_stock_batches')
        .update({ quantity: update.newQuantity, status })
        .eq('id', update.batchId);
    }

    // Create assignment record
    const { error } = await supabase
      .from('event_catering_assignments')
      .insert({
        pta_id: ptaId,
        event_id: eventId,
        new_stock_item_id: stockItemId,
        quantity
      });

    if (error) {
      toast({ title: "Error", description: "Failed to create assignment", variant: "destructive" });
      return false;
    }

    await logActivity('assigned_to_event', item.item_name, {
      quantity,
      eventName: eventName || 'Unknown Event',
      details: { unit_type: item.unit_type }
    });

    toast({ title: "Stock Assigned", description: `${formatQuantityWithUnit(quantity, item.unit_type)} assigned to event` });
    await fetchStockItems();
    return true;
  };

  // Return stock from event - adds to soonest expiring batch
  const returnFromEvent = async (
    stockItemId: string,
    assignmentId: string,
    returnQuantity: number,
    currentAssignedQuantity: number,
    eventName?: string
  ): Promise<boolean> => {
    if (!ptaId) return false;

    const item = stockItems.find(i => i.id === stockItemId);
    if (!item) {
      toast({ title: "Error", description: "Stock item not found", variant: "destructive" });
      return false;
    }

    if (returnQuantity > currentAssignedQuantity) {
      toast({ title: "Error", description: "Cannot return more than assigned", variant: "destructive" });
      return false;
    }

    // Find the soonest expiring batch to inherit its expiry date
    const sortedBatches = [...item.batches].sort((a, b) => {
      if (a.expiry_date && b.expiry_date) {
        return new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime();
      }
      if (a.expiry_date) return -1;
      if (b.expiry_date) return 1;
      return 0;
    });

    const earliestExpiryBatch = sortedBatches[0];
    const returnEventName = eventName || 'Unknown Event';
    const returnBatchNumber = `Return: ${returnEventName}`.substring(0, 50);

    // Create a new batch for the returned stock instead of merging
    const { error: batchError } = await supabase
      .from('catering_stock_batches')
      .insert({
        stock_item_id: stockItemId,
        pta_id: ptaId,
        batch_number: returnBatchNumber,
        quantity: returnQuantity,
        expiry_date: earliestExpiryBatch?.expiry_date || null,
        status: 'in_stock'
      });

    if (batchError) {
      toast({ title: "Error", description: "Failed to create return batch", variant: "destructive" });
      return false;
    }

    // Update or delete the assignment
    const newAssignedQuantity = currentAssignedQuantity - returnQuantity;
    if (newAssignedQuantity <= 0) {
      await supabase
        .from('event_catering_assignments')
        .delete()
        .eq('id', assignmentId);
    } else {
      await supabase
        .from('event_catering_assignments')
        .update({ quantity: newAssignedQuantity })
        .eq('id', assignmentId);
    }

    await logActivity('returned_from_event', item.item_name, {
      batchNumber: returnBatchNumber,
      quantity: returnQuantity,
      eventName: returnEventName,
      details: { unit_type: item.unit_type }
    });

    toast({ 
      title: "Stock Returned", 
      description: `${formatQuantityWithUnit(returnQuantity, item.unit_type)} returned as new batch "${returnBatchNumber}"` 
    });
    await fetchStockItems();
    return true;
  };

  return {
    stockItems,
    customSuppliers,
    loading,
    fetchStockItems,
    addCustomSupplier,
    getAllSupplierOptions,
    addStockItem,
    updateStockItem,
    deleteStockItem,
    addBatch,
    updateBatch,
    deleteBatch,
    assignToEvent,
    returnFromEvent
  };
}
